import styled from "@emotion/styled";
import type { TextFieldProps } from "@mui/material";
import { TextField } from "@mui/material";
import { useField } from "formik";

import { useFieldContext } from "../FormFieldContext";
import { useCommitChange, useFieldLabelAndDescription, useFieldPlaceholder } from "./helpers";

export const Textarea = styled(TextField)`
  width: 100%;
  resize: none;
`;

type FieldTextareaProps = Omit<
  TextFieldProps,
  "id" | "name" | "value" | "onBlur" | "onChange" | "onKeyPress" | "label" | "helpText"
> & {
  onChangeCommitted?: (value?: string, e?: React.FocusEvent<HTMLInputElement>) => void;
};

export const FieldTextarea = (props: FieldTextareaProps) => {
  const { onChangeCommitted, minRows = 1, maxRows = 5, ...otherProps } = props;

  const commitChange = useCommitChange();
  // const handleBlurOnEnterKey = useBlurOnEnterKey();

  const { controlId, dataTest, name } = useFieldContext();
  const [{ value, onChange }] = useField(name);

  const fieldPlaceholder = useFieldPlaceholder(name);
  const [label, helperText] = useFieldLabelAndDescription(name);

  const handleBlur: TextFieldProps["onBlur"] = () => {
    onChangeCommitted?.(value);
    commitChange();
  };

  return (
    <Textarea
      size="small"
      {...otherProps}
      id={controlId}
      name={name}
      value={value}
      placeholder={props.placeholder ?? fieldPlaceholder}
      onBlur={handleBlur}
      onChange={onChange}
      slotProps={{
        ...props.slotProps,
        input: {
          ...props.slotProps?.input,
          // onKeyPress: handleBlurOnEnterKey, TEMP: Removing blur on Enter. It feels more natural to me to add a new line, but needs design discussion.
          // @ts-ignore
          "data-test": `${dataTest ?? name}-textarea`,
        },
      }}
      multiline={!!(props.minRows || props.maxRows)}
      label={label}
      helperText={helperText}
      minRows={minRows}
      maxRows={minRows > maxRows ? undefined : maxRows}
    />
  );
};
